import { z } from "zod";
import { exerciseClip, exerciseImageHash, exerciseListItem, exerciseTracking, } from "./exercise-list";
import { catalogueLocale, exerciseCategory, laterality, loadMode, muscleRole, } from "./vocabulary";
/**
 * One exercise, in full — the wire shape behind the detail screen (`T49`).
 *
 * ── WHY IT IS A SEPARATE ENDPOINT AND NOT A FATTER LIST ITEM ────────────────
 * Everything below that the list does not already carry — the instruction
 * steps, the coaching tip, every muscle with its activation, both clips rather
 * than the one thumbnail — is a JOIN that fans OUT: one exercise has several
 * muscle rows and two video rows. Carrying it in the list would multiply the
 * rows read by every page, and D1 bills rows read (`T-6`). A detail request is
 * one exercise, once, when someone actually opens it.
 *
 * ── THE TWO CLIPS ARE SEPARATE FIELDS, NOT ONE `clip` ───────────────────────
 * The list resolves a SINGLE clip (male, then female) because a card shows one
 * thumbnail. The detail screen offers the choice, so it needs to know which of
 * the two exist: 1,655 exercises have only the male demonstration and 555 only
 * the female one, and the toggle may appear only where both are present. A
 * single pre-resolved clip cannot express that, and a toggle that switched to
 * a clip that does not exist is a player pointed at a 404.
 */
/** Query parameters for the detail route. Only the locale narrows anything. */
export const exerciseDetailQuery = z.object({
    /**
     * Absent means English, which is also the last level of the fallback — so no
     * request can produce an unnamed exercise or an untitled step list.
     */
    locale: catalogueLocale.optional(),
});
const namedRef = z.object({ id: z.int().positive(), name: z.string().min(1) });
/**
 * One muscle this exercise works, with the share of the work it does.
 *
 * ── `role` IS THE FIELD THE SCREEN GROUPS BY, AND IT IS NOT COSMETIC ────────
 * `target` and `synergist` answer different questions — "what am I training"
 * against "what else is loaded" — and the catalogue's filters already treat
 * them asymmetrically (a region filter accepts only `target`; a specific-muscle
 * filter accepts either). The detail screen labels them `ALV` and `SIN`, the
 * abbreviations a Brazilian gym actually uses (`docs/domain/GLOSSARY.md`).
 *
 * ── `activationPct` SUMS TO 100 — FOR 2,996 OF THE 3,001, EXACTLY ──────────
 * Measured over the whole catalogue rather than assumed, and the exceptions
 * matter to whoever renders it: 126 of the 9,001 rows are FRACTIONAL (`16.67`
 * for a sixth, `14.28` for a seventh), and five exercises' shares total 99.96,
 * 100.02 or 100.10 rather than 100 — the same two-decimal rounding,
 * accumulated. Recorded as `T-50`; the DISPLAY consequence is `T-70`.
 *
 * So this is a `number` and not an `int`: rounding here would destroy the
 * information a client needs to apportion the shares back to a whole 100. The
 * mobile app does that in `displayedActivation`, and the reason it cannot be a
 * per-row `Math.round` is those two facts.
 */
export const exerciseMuscleActivation = z.object({
    id: z.int().positive(),
    /** Localized through the three-level fallback — `Posteriores`, not a gloss. */
    name: z.string().min(1),
    role: muscleRole,
    activationPct: z.number().positive().max(100),
});
/**
 * Both demonstrations, each independently absent.
 *
 * ── BOTH `null` IS A REAL STATE AND MUST STAY RENDERABLE ────────────────────
 * `Sled Backward Angled Calf Raise` has neither. Its only clip was a
 * mislabelled copy of the 45-degree hyperextension's female demonstration — a
 * different movement, on a different machine, loading different muscles — and
 * it was withheld on purpose, with the decision recorded in
 * `catalog_video_coverage_gap`. In a training app a wrong demonstration teaches
 * wrong form, and wrong form under load is an injury, so showing nothing is
 * strictly better than showing something confidently wrong.
 *
 * A screen receiving two nulls therefore renders "no demonstration available"
 * and stops. Not an error, not a spinner, and above all never another
 * exercise's clip.
 */
export const exerciseClips = z.object({
    male: exerciseClip.nullable(),
    female: exerciseClip.nullable(),
});
/**
 * One ANATOMICAL muscle this exercise works (`D196`) — a head or a portion
 * (the clavicular pec, the rear deltoid) or a muscle the twelve have no name
 * for (the erectors, the adductors) — and the layers that paint it.
 *
 * ── FOR SHOWING, NEVER FOR COUNTING ─────────────────────────────────────────
 * The engine's vocabulary stays the twelve in {@link exerciseMuscleActivation}
 * (`D146`): selection, volume per muscle and the injury rules read only
 * those. These carry no activation share on purpose — the source has none,
 * and a share invented here would read as a measurement. A screen paints them
 * on the body map and may say "more clavicular"; it must never total them.
 *
 * - `parentMuscleId` is the training muscle this one rolls up to, so a body
 *   map can light the twelve's chip when one of its heads is worked. `null`
 *   where none of the twelve names it (the neck, the hip flexors).
 * - `overlays` are the layer names in the app's own `public/anatomy/`, one
 *   list per body, because the male and female figures are drawn separately
 *   and do not share layer names. Either list may be empty: a muscle drawn on
 *   one body and not the other is real data, not an error.
 */
export const exerciseMuscleDetail = z.object({
    id: z.int().positive(),
    /** The stable English key (`adductor_longus`). Never displayed. */
    key: z.string().min(1),
    /** Localized through the same three-level fallback as every label. */
    name: z.string().min(1),
    role: muscleRole,
    parentMuscleId: z.int().positive().nullable(),
    overlays: z.object({
        male: z.array(z.string()),
        female: z.array(z.string()),
    }),
});
export const exerciseDetail = z.object({
    id: z.string().min(1),
    /** Already through the three-level fallback. The client never falls back. */
    name: z.string().min(1),
    category: exerciseCategory,
    difficultyLevel: z.int().min(1).max(5),
    /**
     * The steps, as an ARRAY — never the source's single blob with `"1- "` glued
     * to the front of each step. Ingestion (`T45`) strips those prefixes at load
     * time precisely so that no client parses display text to find a list, and
     * so that a Portuguese step numbered `"1- "` and an English one numbered
     * `"1. "` are the same shape here.
     *
     * May be empty: eleven of the source's languages carry only names. Neither
     * `en` nor `pt` is one of them, but a screen must render an empty list as an
     * absent section rather than as a failure.
     */
    instructions: z.array(z.string().min(1)),
    /** The one-line cue. `null` where the locale carries no tip. */
    coachingTip: z.string().min(1).nullable(),
    /** Target muscles first, then synergists; each group by activation, desc. */
    muscles: z.array(exerciseMuscleActivation),
    equipment: namedRef,
    /** 106 of 3,001 exercises need a second piece of equipment. */
    secondaryEquipment: namedRef.nullable(),
    /**
     * The machine type, where there is one — 124 exercises share `Leverage
     * machine` as equipment, which is too coarse to tell someone which machine to
     * walk to, so those were subdivided into 62 machine types by hand.
     *
     * Keyed on an ID rather than on the English text, which is how the source
     * keyed its machine translations: a spelling fix on one side silently orphans
     * the translation on the other.
     */
    machine: namedRef.nullable(),
    clips: exerciseClips,
    /** Optional for the deploy window only, as on the list row (`D151`). */
    tracks: exerciseTracking.optional(),
    // ── THE SECOND LAYER (`D196`) ────────────────────────────────────────────
    // Every field below is OPTIONAL and stays so: a new output field must not
    // break an app talking to a server from before `0027`, or one rolled back
    // to it (`D172`, `backward-compat.test.ts`). Absent and `null` are both
    // "nothing to show"; a screen never branches on which.
    /** How a logged load reads; absent or `null` is `total`. See `loadMode`. */
    loadMode: loadMode.nullable().optional(),
    /**
     * Whether the sides work in turn (`D232`): `unilateral` is logged per
     * side. Absent or `null` is unknown, read as before.
     */
    laterality: laterality.nullable().optional(),
    /** The figure on a transparent background: `cutouts/<hash>.webp`. */
    cutoutHash: exerciseImageHash.nullable().optional(),
    /**
     * The start and finish poses side by side, ~800 px wide, transparent:
     * `frames/<hash>.webp`. A still the sheet can show where a clip would cost
     * a download, and the one image that says where the movement ENDS.
     */
    framesHash: exerciseImageHash.nullable().optional(),
    /**
     * A real person performing it, as a YouTube video id — not a URL, so the
     * app decides how to open it and no server-supplied link reaches a WebView.
     * `null` for most exercises.
     */
    youtubeId: z.string().min(1).nullable().optional(),
    /**
     * The anatomical muscles behind {@link exerciseDetail.shape.muscles}, for
     * the body map. Targets first, then synergists, then by id. Empty where the
     * layer has none for this exercise; absent from an older server.
     */
    muscleDetails: z.array(exerciseMuscleDetail).optional(),
    /**
     * Whether a strength standard exists for this lift (`D208`) — the
     * population model a "strength level" is read against. A flag and not the
     * model: the coefficients are thirty numbers per sex that only the server
     * evaluates, and the sheet needs only to know whether to offer the level.
     */
    hasStrengthStandard: z.boolean().optional(),
    /**
     * "Exercícios parecidos" (`D231`): the closest strength exercises by what
     * they train — the same movement, not one shared muscle — as list rows, so
     * the sheet draws them with the catalogue's own card. At most six, closest
     * first; empty for an exercise without neighbours and for every
     * non-strength one. OPTIONAL and stays so: absent from an older server,
     * and an older app ignores it (`D172`).
     */
    similar: z.array(exerciseListItem).optional(),
});
export const exerciseDetailOutput = exerciseDetail;
/**
 * Which demonstrations this exercise actually has.
 *
 * ── THE TOGGLE RULE, WRITTEN ONCE ───────────────────────────────────────────
 * It exists only where BOTH clips do. Writing that test inline on a screen is
 * how it ends up as `clips.male !== null` on one branch and
 * `clips.female !== null` on another, and the failure is a toggle that switches
 * to nothing. Here it is one function, and it is what both the screen and its
 * tests read.
 */
export function availableClipGenders(clips) {
    const available = [];
    // Male first: it is the canonical demonstration everywhere else in the app,
    // so it is also the one a toggle should open on.
    if (clips.male !== null) {
        available.push("male");
    }
    if (clips.female !== null) {
        available.push("female");
    }
    return available;
}
/**
 * The path for one exercise.
 *
 * ── THE ID IS ENCODED, ALWAYS ───────────────────────────────────────────────
 * Exercise ids are the source's opaque hex text today, so encoding changes
 * nothing for any row that exists. That is exactly why it must be written now:
 * the day an id contains a character that needs escaping, an unencoded path is
 * a 404 that reads as a deleted exercise rather than as a malformed URL.
 */
export function exerciseDetailPath(id) {
    return `/catalog/exercises/${encodeURIComponent(id)}`;
}
/**
 * The same path with the locale attached, or bare when the locale is absent.
 *
 * The return type is `\`/${string}\`` and not `string`: the mobile client's
 * `Fetcher` takes an absolute path, so a builder that ever produced a relative
 * one would be a compile error here rather than a request resolved against
 * whatever base URL happened to be current.
 */
export function exerciseDetailPathWithQuery(id, locale) {
    const path = exerciseDetailPath(id);
    return locale === undefined ? path : `${path}?locale=${locale}`;
}
//# sourceMappingURL=exercise-detail.js.map